import { normalizeSessionDetails } from "../channel/normalizePayload.js";

const SINGLE_FIELD_EVENT_TYPES = ["detail", "hosting", "streaming", "setting"];

// The backend sends both bare event types ("detail", "hosting", "streaming",
// "setting") and "cme"-prefixed ones (e.g. "cmestreaming") for the exact
// same single-field updates. Normalize the prefixed form to the bare one
// before checking - but "cmestatus" and "cmeall" are NOT single-field
// updates (status folds in separately; cmeall is a full snapshot), so leave
// those two untouched.
function normalizeCmeEventType(eventType) {
  if (typeof eventType !== "string") return eventType;
  if (eventType === "cmestatus" || eventType === "cmeall") return eventType;
  return eventType.startsWith("cme") ? eventType.slice(3) : eventType;
}

// "cmehostinghealth" (Mux live-stream telemetry) rides the same session-details channel
// as the session payload. Its data carries its OWN `status` ("ready", "active", ...),
// which is Mux's live-stream status and not the numeric session status (1-7). Running it
// through the status -> detail.status fold used to clobber detail.status and drop the
// dashboard on the "Session unavailable" screen, so health is written into its own
// data.health group and never touches detail/status.
const HEALTH_EVENT_TYPES = new Set(["cmehostinghealth", "hostinghealth", "cmehealth", "health"]);

// `raw` must already have gone through adaptFlatCmeEvent() so the doubly-nested
// { data: { event_type, data } } envelope unwraps first. Returns the health payload
// object, or null when this is not a health event.
function healthPayloadOf(raw) {
  if (!raw || typeof raw !== "object" || !HEALTH_EVENT_TYPES.has(raw.event_type)) return null;
  const payload = raw.data;
  return payload && typeof payload === "object" && !Array.isArray(payload) ? payload : null;
}

function adaptFlatCmeEvent(raw) {
  if (!raw || typeof raw !== "object") return raw;
  if (raw.data && typeof raw.data === "object" && raw.data.event_type && raw.data.data) {
    raw = { ...raw, ...raw.data };
  }
  const eventType = normalizeCmeEventType(raw.event_type);
  if (!SINGLE_FIELD_EVENT_TYPES.includes(eventType)) return raw;
  const data = raw.data;
  if (!data || typeof data !== "object" || eventType in data) return raw;
  return { ...raw, data: { [eventType]: data } };
}

export function extractCmeFields(raw) {
  raw = adaptFlatCmeEvent(raw);
  if (!raw || typeof raw !== "object") return {};
  // Health telemetry is its own group: never fold its `status` into detail.
  const health = healthPayloadOf(raw);
  if (health) return { health };
  const wrap = raw.data && typeof raw.data === "object" ? raw.data : null;
  const out = {};
  const FIELDS = ["detail", "hosting", "streaming", "setting", "streamToUser", "endStram", "health", "status"];
  if (wrap) for (const f of FIELDS) if (f in wrap) out[f] = wrap[f];
  for (const f of FIELDS) if (f in raw) out[f] = raw[f];
  if (out.status !== undefined) {
    out.detail = { ...(out.detail || {}), status: out.status };
    delete out.status;
  }
  return out;
}

function mergeGroup(existing, incoming) {
  return { ...(existing || {}), ...(incoming || {}) };
}

function mergeSetting(existing, incoming) {
  if (!incoming) return existing;
  const out = { ...(existing || {}), ...incoming };
  for (const k of ["branding", "navbar", "subNavbar", "engagement"]) {
    if (incoming[k]) out[k] = { ...(existing?.[k] || {}), ...incoming[k] };
  }
  return out;
}

export function buildSessionCacheKey(sessionId) {
  return ["session", Number(sessionId)];
}

export const GROUP_DATA_PATHS = {
  detail: ["data.detail"],
  hosting: ["data.hosting"],
  streaming: ["data.streaming"],
  setting: ["data.setting"],
  // endStram (Stop Live) shares the group: the admin always publishes the two together,
  // so they are ordered as one - never one flag from a newer message, the other older.
  streamToUser: ["data.streamToUser", "data.endStram"],
  health: ["data.health"],
};

export const SESSION_FIELD_GROUPS = Object.keys(GROUP_DATA_PATHS);

// The admin UI reads !!session.data.streamToUser (Live step, "Go Live" button), so the
// key is always materialised - false when the payload never carried it. A missing key
// was indistinguishable from "off" in the UI, but showed up as "not present at all" in
// the session cache while the viewer side had streamToUser: true.
// Parses the streamToUser flag defensively: Boolean("false") === true, so plain
// Boolean() would flip the Go-Live state ON for a string "false"/"0".
function toStreamToUserBool(value) {
  if (typeof value === "string") {
    return ["true", "1", "yes"].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
}

// Resolved exactly like the viewer side (cme-core utils/session.js isStreamStarted): a
// top-level flag wins; without one, a snapshot can carry it nested as
// streaming.streamToUser. Defaulting that case to false made the admin show "Go Live"
// while every viewer was already live from the same cme-common snapshot.
// endStram (Stop Live: "the stream has ended") is materialised the same way - always a
// boolean, false when the payload never carried it (a session that never went live).
function withStreamToUser(snapshot) {
  if (!snapshot || typeof snapshot !== "object" || !snapshot.data || typeof snapshot.data !== "object") return snapshot;
  const current = snapshot.data.streamToUser;
  const ended = snapshot.data.endStram;
  if (typeof current === "boolean" && typeof ended === "boolean") return snapshot;
  const resolved =
    typeof current === "boolean"
      ? current
      : current != null
        ? toStreamToUserBool(current)
        : toStreamToUserBool(snapshot.data.streaming?.streamToUser ?? false);
  return {
    ...snapshot,
    data: { ...snapshot.data, streamToUser: resolved, endStram: toStreamToUserBool(ended ?? false) },
  };
}

export function toValidSnapshot(raw, sessionId) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.kind === "stream-status") return null;
  raw = adaptFlatCmeEvent(raw);

  const wrap = raw.data && typeof raw.data === "object" ? raw.data : null;
  const detail = (wrap?.detail && typeof wrap.detail === "object") ? wrap.detail : (raw.detail && typeof raw.detail === "object" ? raw.detail : null);
  if (!detail) return null;

  const hasTitle = typeof detail.title === "string" && detail.title.trim();
  // Number(null)/Number("")/Number([]) all yield 0 and would pass a bare
  // Number.isFinite check — accept only real numbers or non-empty numeric strings.
  const rawStatus = detail.status;
  const statusNum = Number(rawStatus);
  const hasStatus =
    Number.isFinite(statusNum) &&
    (typeof rawStatus === "number" ||
      (typeof rawStatus === "string" && rawStatus.trim() !== ""));
  if (!hasTitle && !hasStatus) return null;

  const norm = { ...raw };
  if (sessionId != null && norm.sessionId == null) norm.sessionId = Number(sessionId);
  return withStreamToUser(normalizeSessionDetails(norm));
}

export function isPartialCmeEvent(raw) {
  raw = adaptFlatCmeEvent(raw);
  if (!raw || typeof raw !== "object") return false;
  // Health telemetry is always a partial of its own group, even though its payload
  // carries a `status` key that would otherwise classify it as a detail update.
  if (healthPayloadOf(raw)) return true;
  // "cmeall" IS the backend's full snapshot by contract. Its groups may legitimately
  // arrive null (streaming/hosting are null until a vendor session exists), which the
  // four-group check below would read as "partial". A partial with nothing cached yet
  // is buffered as "pending base" and only flushed once a full snapshot lands - so the
  // session cache stayed empty forever and the dashboard showed "Session not found".
  if (normalizeCmeEventType(raw.event_type) === "cmeall") return false;
  const d = raw.data && typeof raw.data === "object" ? raw.data : null;

  const isFlatFull = (d && ("playerConfig" in d || "componentConfig" in d)) || ("playerConfig" in raw || "componentConfig" in raw);
  if (isFlatFull) return false;

  const hasDetail = (d && "detail" in d) || "detail" in raw;
  const hasStatus = (d && "status" in d) || "status" in raw;
  const hasHosting = (d && "hosting" in d) || "hosting" in raw;
  const hasStreaming = (d && "streaming" in d) || "streaming" in raw;
  const hasSetting = (d && "setting" in d) || "setting" in raw;
  const hasStreamToUser = (d && "streamToUser" in d) || "streamToUser" in raw;
  const hasEndStram = (d && "endStram" in d) || "endStram" in raw;
  // Key presence, not truthiness: a snapshot whose group is null ("no streaming yet")
  // is still a snapshot - truthiness made it a partial, which then buffered forever.
  const holds = (obj, key) => !!obj && key in obj;
  const isFull =
    (d && holds(d, "detail") && holds(d, "hosting") && holds(d, "streaming") && holds(d, "setting")) ||
    (holds(raw, "detail") && holds(raw, "hosting") && holds(raw, "streaming") && holds(raw, "setting"));
  return (hasDetail || hasStatus || hasHosting || hasStreaming || hasSetting || hasStreamToUser || hasEndStram) && !isFull;
}

export function applyPartialCmeUpdate(sessionId, raw, existingData = null) {
  const existing = existingData;
  if (!existing) return null;

  const { detail, hosting, streaming, setting, streamToUser, endStram, health } = extractCmeFields(raw);
  if (!detail && !hosting && !streaming && !setting && streamToUser === undefined && endStram === undefined && !health) {
    return null;
  }

  const merged = {
    ...existing,
    data: {
      ...existing.data,
      ...(detail ? { detail: mergeGroup(existing.data?.detail, detail) } : {}),
      ...(hosting ? { hosting: mergeGroup(existing.data?.hosting, hosting) } : {}),
      ...(streaming ? { streaming: mergeGroup(existing.data?.streaming, streaming) } : {}),
      ...(setting ? { setting: mergeSetting(existing.data?.setting, setting) } : {}),
      ...(streamToUser !== undefined ? { streamToUser: toStreamToUserBool(streamToUser) } : {}),
      ...(endStram !== undefined ? { endStram: toStreamToUserBool(endStram) } : {}),
      // Health is a whole snapshot per tick - replace, never merge into detail.
      ...(health ? { health } : {}),
    },
  };
  if (sessionId != null && merged.sessionId == null) merged.sessionId = Number(sessionId);
  return withStreamToUser(normalizeSessionDetails(merged));
}

function touchedGroupsForPartial(raw) {
  raw = adaptFlatCmeEvent(raw);
  if (healthPayloadOf(raw)) return ["health"];
  const d = raw.data && typeof raw.data === "object" ? raw.data : null;
  const has = (key) => (d && key in d) || key in raw;
  const groups = [];
  if (has("detail") || has("status")) groups.push("detail");
  if (has("hosting")) groups.push("hosting");
  if (has("streaming")) groups.push("streaming");
  if (has("setting")) groups.push("setting");
  if (has("streamToUser") || has("endStram")) groups.push("streamToUser");
  if (has("health")) groups.push("health");
  return groups;
}

// A full snapshot only stamps the groups it actually carries. cmeall has no telemetry
// (computeCandidate deliberately carries the previous health forward), so stamping
// `health` would make an older - still valid - health tick read as stale and drop it.
// That is exactly what channel history does: it replays oldest-first, so the health tick
// is applied BEFORE the snapshot that follows it, gets buffered as a pending partial,
// and is then discarded as stale when the snapshot stamps a newer timestamp on health.
function fullSnapshotGroups(raw) {
  const groups = [...SESSION_FIELD_GROUPS];
  if (extractCmeFields(raw).health === undefined) {
    const i = groups.indexOf("health");
    if (i !== -1) groups.splice(i, 1);
  }
  return groups;
}

export function createCmeCommonChannelConfig(sessionId) {
  return {
    groups: SESSION_FIELD_GROUPS,
    fieldsForGroup(group) {
      return GROUP_DATA_PATHS[group] || [];
    },
    classify(raw) {
      if (!raw || typeof raw !== "object" || raw.kind === "stream-status") return { kind: "ignore", groups: [] };
      if (isPartialCmeEvent(raw)) {
        const groups = touchedGroupsForPartial(raw);
        return groups.length ? { kind: "partial", groups } : { kind: "ignore", groups: [] };
      }
      const full = toValidSnapshot(raw, sessionId);
      return full ? { kind: "full", groups: fullSnapshotGroups(raw) } : { kind: "ignore", groups: [] };
    },
    computeCandidate({ raw, current, kind }) {
      if (kind === "full") {
        const snap = toValidSnapshot(raw, sessionId);
        // A cmeall snapshot does not carry telemetry, so keep the last known health
        // instead of wiping the key until the next health tick repopulates it.
        const previousHealth = current?.data?.health ?? undefined;
        const carryHealth = snap?.data?.health == null ? previousHealth : undefined;
        if (snap && carryHealth !== undefined) return { ...snap, data: { ...snap.data, health: carryHealth } };
        return snap;
      }
      return applyPartialCmeUpdate(sessionId, raw, current);
    },
  };
}
